/**
 * Client HTTP de la plateforme.
 *
 * ── Où vit le jeton d'accès ──────────────────────────────────────────
 * **En mémoire, jamais dans `localStorage`** (skill `securite-donnees-sante`,
 * CCT § 5.1). Un jeton en stockage persistant est lisible par n'importe quel
 * script de la page et survit à la fermeture de l'onglet.
 *
 * La contrepartie est assumée : un rechargement de page perd le jeton. C'est
 * le cookie `httpOnly` de rafraîchissement — invisible au JavaScript — qui
 * permet de rouvrir une session au démarrage, via `restaurerSession()`.
 *
 * ── Rafraîchissement ─────────────────────────────────────────────────
 * Un 401 sur une route authentifiée déclenche UNE tentative de
 * rafraîchissement, puis rejoue la requête. Les appels concurrents partagent
 * la même promesse : sans ça, cinq requêtes qui expirent ensemble
 * déclencheraient cinq rotations, et la détection de réutilisation de jeton
 * côté serveur révoquerait toute la famille.
 */
import { noterRequeteEnEchec } from '../signalement/contexte.js'
import { ZodError, type ZodType } from 'zod'
import {
  DUREE_JETON_ACCES_MINUTES,
  ENTETE_ARRIERE_PLAN,
  RAISON_SESSION_INACTIVE,
} from '@afs/shared'
import {
  ErreurApi,
  ErreurVersionObsolete,
  erreurDelaiDepasse,
  erreurReseau,
  type CodeErreurApi,
} from './erreurs.js'

const BASE = '/api/v1'

/**
 * Au-delà, le serveur est tenu pour injoignable (S77, C1). Sans délai, un
 * `fetch` parti quand la liaison tombe — Wi-Fi du site toujours branché —
 * attend le délai TCP du navigateur, plusieurs minutes : chargement sans fin,
 * aucune erreur, donc ni bandeau ni accueil hors ligne. Un dépôt de fichier
 * (DNS, photo) a droit à plus : il monte sur une liaison lente.
 */
export const DELAI_REQUETE_MS = 15_000
export const DELAI_DEPOT_MS = 120_000
/** Un traitement serveur long : validation ou annulation d'un import DNS, export. */
export const DELAI_TRAITEMENT_LONG_MS = 120_000

/** Routes qui ne doivent jamais déclencher de tentative de rafraîchissement. */
// `/ecran-appel/` : la télé de salle d'attente n'a pas de session (ADR 0012) —
// son refus veut dire « écran non activé », pas « jeton à renouveler ».
const SANS_RAFRAICHISSEMENT = [
  '/auth/connexion',
  '/auth/rafraichir',
  '/auth/2fa/verification',
  '/ecran-appel/',
]

let jetonAcces: string | null = null
let rafraichissementEnCours: Promise<Renouvellement> | null = null

let jetonRecuLe = 0

export function definirJetonAcces(jeton: string | null): void {
  jetonAcces = jeton
  if (jeton !== null) jetonRecuLe = Date.now()
  // Une session neuve pourra, à son tour, être signalée close.
  if (jeton !== null) sessionFermeeSignalee = false
}

// ── Activité de l'agent (décision 417) ─────────────────────────────────
// Le serveur ferme une session de poste après 120 min sans action. Une
// relecture qu'aucun geste n'a déclenchée (signal temps réel, retour du
// réseau, focus) part marquée « arrière-plan » et ne repousse rien : sans
// ça, la file d'un site animé tiendrait ouvert un poste oublié toute la journée.

/** Une requête partie dans cette fenêtre après un geste est l'effet de ce geste. */
export const FENETRE_ACTION_MS = 60_000
let derniereInteraction = Number.NEGATIVE_INFINITY

export function noterInteraction(): void {
  derniereInteraction = Date.now()
}

/** À brancher une fois, au démarrage : clavier, clic, toucher, molette. */
export function suivreLesInteractions(cible: Pick<Window, 'addEventListener'>): void {
  for (const type of ['pointerdown', 'keydown', 'wheel'] as const) {
    cible.addEventListener(type, noterInteraction, { capture: true, passive: true })
  }
}

function enArrierePlan(): boolean {
  return Date.now() - derniereInteraction > FENETRE_ACTION_MS
}

// ── Session close (décision 417) ───────────────────────────────────────
// Un refus du rafraîchissement, ou la fermeture 4001 de la connexion temps
// réel, veut dire que la session n'existe plus côté serveur. L'application
// est prévenue une seule fois : elle vide la session et renvoie à la
// connexion avec le motif, au lieu de laisser chaque écran afficher son erreur.

export const MOTIF_SESSION_FERMEE = 'Votre session a été fermée. Reconnectez-vous.'
const ecouteursSessionFermee = new Set<(motif: string) => void>()
let sessionFermeeSignalee = false

export function surSessionFermee(ecouteur: (motif: string) => void): () => void {
  ecouteursSessionFermee.add(ecouteur)
  return () => {
    ecouteursSessionFermee.delete(ecouteur)
  }
}

export function signalerSessionFermee(motif: string | null | undefined): void {
  if (sessionFermeeSignalee) return
  sessionFermeeSignalee = true
  const texte = motif !== null && motif !== undefined && motif !== '' ? motif : MOTIF_SESSION_FERMEE
  for (const ecouteur of ecouteursSessionFermee) ecouteur(texte)
}

export function jetonAccesCourant(): string | null {
  return jetonAcces
}

/** Une minute de marge avant l'expiration du jeton d'accès (15 min). */
const MARGE_PEREMPTION_MS = 60_000

/**
 * Le jeton manque, ou a presque fini sa vie. Son âge se compte sur l'horloge
 * du poste depuis sa réception, jamais contre l'heure du serveur : une
 * horloge de poste déréglée ne change rien.
 *
 * Sert à la reconnexion temps réel : renouveler à chaque reconnexion ferait
 * tourner le cookie de rafraîchissement pour rien, et deux onglets du même
 * poste qui reviennent ensemble présenteraient le même cookie — la seconde
 * présentation passerait pour un vol et révoquerait la session.
 */
export function jetonAPerimer(): boolean {
  return (
    jetonAcces === null ||
    Date.now() - jetonRecuLe > DUREE_JETON_ACCES_MINUTES * 60_000 - MARGE_PEREMPTION_MS
  )
}

interface Envoi {
  readonly methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly corps?: unknown
  /**
   * Envoi `multipart/form-data` — un fichier déposé. Le navigateur pose
   * lui-même l'en-tête et sa frontière : on ne l'écrit surtout pas.
   */
  readonly formulaire?: FormData
  /**
   * Jeton à présenter à la place de celui de la session — le jeton
   * temporaire du second facteur, qui n'ouvre aucune session.
   */
  readonly jeton?: string | undefined
  /**
   * Délai propre à cet appel, pour un traitement serveur long (validation
   * d'un import DNS, export). Absent : `DELAI_REQUETE_MS`, ou `DELAI_DEPOT_MS`
   * pour un dépôt de fichier.
   */
  readonly delaiMs?: number | undefined
}

interface OptionsRequete<T> extends Envoi {
  /** Schéma de la réponse. Absent = réponse ignorée (204). */
  readonly schema?: ZodType<T>
}

async function envoyer(chemin: string, options: Envoi): Promise<Response> {
  const entetes: Record<string, string> = { Accept: 'application/json' }
  const jeton = options.jeton ?? jetonAcces
  if (jeton) entetes.Authorization = `Bearer ${jeton}`
  if (enArrierePlan()) entetes[ENTETE_ARRIERE_PLAN] = '1'
  if (options.corps !== undefined) entetes['Content-Type'] = 'application/json'

  const methode = options.methode ?? 'GET'
  // Un minuteur plutôt qu'`AbortSignal.timeout` : il se règle sur la même
  // horloge que le reste de la page, et se teste.
  const abandon = new AbortController()
  const minuteur = setTimeout(
    () => {
      abandon.abort()
    },
    options.delaiMs ?? (options.formulaire === undefined ? DELAI_REQUETE_MS : DELAI_DEPOT_MS),
  )
  try {
    return await fetch(`${BASE}${chemin}`, {
      method: methode,
      signal: abandon.signal,
      headers: entetes,
      // Indispensable : le cookie de rafraîchissement ne part pas sans lui.
      credentials: 'include',
      // `null` et non `undefined` : avec `exactOptionalPropertyTypes`,
      // `RequestInit.body` accepte `BodyInit | null`, jamais `undefined`.
      body:
        options.formulaire ?? (options.corps === undefined ? null : JSON.stringify(options.corps)),
    })
  } catch {
    throw abandon.signal.aborted ? erreurDelaiDepasse(methode !== 'GET') : erreurReseau()
  } finally {
    clearTimeout(minuteur)
  }
}

export async function versErreur(reponse: Response): Promise<ErreurApi> {
  // Le 429 vient le plus souvent de Caddy, sans corps : sans ce cas, il
  // s'affichait « Une erreur est survenue côté serveur » (recette du 06/10).
  // Celui de l'API porte un message anglais : on le remplace aussi.
  if (reponse.status === 429) {
    const brut = reponse.headers.get('retry-after')
    const reessayerDansS = brut === null ? Number.NaN : Number(brut)
    return new ErreurApi({
      message: 'Trop de tentatives depuis ce poste. Patientez une minute, puis réessayez.',
      code: 'LIMITE',
      statut: 429,
      // Une date HTTP ou un en-tête absent donnent NaN : l'appelant choisit alors son délai.
      reessayerDansS: Number.isFinite(reessayerDansS) ? reessayerDansS : undefined,
    })
  }

  const corps: unknown = await reponse.json().catch(() => null)
  const objet = (corps ?? {}) as Record<string, unknown>
  if (typeof objet.requestId === 'string') noterRequeteEnEchec(objet.requestId)

  return new ErreurApi({
    message:
      typeof objet.message === 'string' ? objet.message : 'Une erreur est survenue côté serveur.',
    code: (typeof objet.code === 'string' ? objet.code : 'VALIDATION') as CodeErreurApi,
    statut: reponse.status,
    regle: typeof objet.regle === 'string' ? objet.regle : undefined,
    requestId: typeof objet.requestId === 'string' ? objet.requestId : undefined,
    raison: typeof objet.raison === 'string' ? objet.raison : undefined,
    details:
      typeof objet.details === 'object' && objet.details !== null
        ? (objet.details as Record<string, unknown>)
        : undefined,
  })
}

/**
 * Issue d'un renouvellement du jeton d'accès.
 *
 * - `renouvele` : un jeton neuf est en mémoire ;
 * - `injoignable` : le serveur n'a pas répondu, ou a répondu en panne (5xx
 *   d'un redéploiement) — la session vaut peut-être encore, on retentera ;
 * - `refuse` : le serveur a refusé — la session est close, il faut se reconnecter.
 */
export type Renouvellement = 'renouvele' | 'injoignable' | 'refuse'

/**
 * Renouvelle le jeton d'accès à partir du cookie de rafraîchissement. Les
 * appels concurrents (requêtes HTTP, connexion temps réel) partagent la même
 * promesse.
 */
export function renouvelerJetonAcces(
  /** Le motif d'un refus déjà reçu, plus précis que celui du rafraîchissement. */
  motifConnu?: string,
): Promise<Renouvellement> {
  rafraichissementEnCours ??= (async (): Promise<Renouvellement> => {
    // Au démarrage, sans jeton, un refus dit seulement « pas de session » : rien à signaler.
    const sessionOuverte = jetonAcces !== null
    try {
      const reponse = await envoyer('/auth/rafraichir', { methode: 'POST' })
      // Seul un 401 ou un 403 dit « session close ». Une panne (5xx d'un
      // redéploiement) ou un 429 du limiteur n'en est pas un : effacer le
      // jeton ici déconnecterait tout le monde à chaque redéploiement.
      const refus = reponse.status === 401 || reponse.status === 403
      if (!reponse.ok && !refus) return 'injoignable'
      const corps = (await reponse.json().catch(() => ({}))) as {
        jetonAcces?: unknown
        message?: unknown
      }
      if (!reponse.ok || typeof corps.jetonAcces !== 'string') {
        definirJetonAcces(null)
        if (sessionOuverte) {
          signalerSessionFermee(
            motifConnu ?? (typeof corps.message === 'string' ? corps.message : null),
          )
        }
        return 'refuse'
      }
      definirJetonAcces(corps.jetonAcces)
      return 'renouvele'
    } catch {
      // Réseau coupé : on ne détruit PAS le jeton courant. Le supprimer
      // déconnecterait l'utilisateur à la première coupure, alors que la
      // session est toujours valable côté serveur.
      return 'injoignable'
    } finally {
      rafraichissementEnCours = null
    }
  })()

  return rafraichissementEnCours
}

/** Vrai si un jeton neuf est en mémoire ; faux si la session est close ou le serveur injoignable. */
async function rafraichir(motifConnu?: string): Promise<boolean> {
  return (await renouvelerJetonAcces(motifConnu)) === 'renouvele'
}

/** Envoie, et rejoue une fois après renouvellement du jeton si la session l'exige. */
async function envoyerAvecRejeu(chemin: string, options: Envoi): Promise<Response> {
  let reponse = await envoyer(chemin, options)

  const rejouable =
    reponse.status === 401 &&
    options.jeton === undefined &&
    !SANS_RAFRAICHISSEMENT.some((route) => chemin.startsWith(route))

  if (rejouable) {
    // La session vient d'être fermée pour inactivité : le rafraîchissement ne
    // trouvera plus qu'une session absente, c'est ce motif-ci qu'on veut lire.
    const refus = await versErreur(reponse.clone())
    const motif = refus.raison === RAISON_SESSION_INACTIVE ? refus.message : undefined
    if (await rafraichir(motif)) reponse = await envoyer(chemin, options)
  }

  if (!reponse.ok) throw await versErreur(reponse)
  return reponse
}

/**
 * Un document servi par l'API (le scan d'un DNS papier) : il exige le jeton,
 * donc ni `<img src>` ni lien direct — on le lit, et l'écran l'affiche depuis
 * une URL d'objet qu'il révoque en partant.
 */
/** Un fichier (image, PDF, CSV). `delaiMs` allonge l'attente d'un export lourd. */
export async function requeteBinaire(
  chemin: string,
  options: { readonly delaiMs?: number } = {},
): Promise<Blob> {
  return (await envoyerAvecRejeu(chemin, options)).blob()
}

export async function requete<T = void>(
  chemin: string,
  options: OptionsRequete<T> = {},
): Promise<T> {
  const reponse = await envoyerAvecRejeu(chemin, options)
  if (!options.schema) return undefined as T

  // La réponse est validée par le schéma partagé : si l'API change sans que
  // le front suive, on le voit ici plutôt qu'au milieu d'un composant.
  // Une réponse que cette version ne comprend pas vient presque toujours d'un
  // déploiement : on le dit en clair au lieu d'afficher le détail de la validation.
  try {
    return options.schema.parse(await reponse.json())
  } catch (erreur) {
    if (erreur instanceof ZodError) throw new ErreurVersionObsolete()
    throw erreur
  }
}

/**
 * Rouvre une session au démarrage de l'application, à partir du seul cookie
 * de rafraîchissement. Appelée une fois, avant le premier rendu utile.
 */
export async function restaurerSession(): Promise<boolean> {
  if (jetonAcces) return true
  return rafraichir()
}
